import { Button, Icon } from "@mui/material";
import { _ } from "js/vendor";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import styled from "styled-components";

import { AiImageStyles, AiImageStyleTypes } from "common/aiConstants";
import { ThemeType } from "common/constants";
import { builtInFonts, obsoleteBuiltInFontIds } from "common/fontConstants";
import { ActionPermissionsObject, WorkspaceResourceType } from "common/interfaces";
import { defaultSimpleTheme } from "common/themes";
import PresentationLibraryController from "js/controllers/PresentationLibraryController";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { getPresentation } from "js/core/models/presentation";
import { Theme } from "js/core/models/theme";
import importPPTTheme from "js/core/utilities/importPPTTheme";
import { delay } from "js/core/utilities/promiseHelper";
import uniqueName from "js/core/utilities/uniqueName";
import { pluralize, trackActivity } from "js/core/utilities/utilities";
import db from "js/db";
import { ChooseThemeDialog } from "js/editor/ThemeEditor/ChooseThemeDialog";
import { GenerateThemeThumbnail } from "js/editor/ThemeEditor/GenerateThemeThumbnail";
import ImportFontsDialog from "js/editor/ThemeEditor/ImportFontsDialog";
import ThemeEditorController from "js/editor/ThemeEditor/ThemeEditorController";
import { DefaultSlides } from "js/editor/ThemeEditor/panes//DefaultSlides";
import ThemeColors from "js/editor/ThemeEditor/panes/ThemeColors";
import ThemeDecoration from "js/editor/ThemeEditor/panes/ThemeDecoration";
import ThemeFooter from "js/editor/ThemeEditor/panes/ThemeFooter";
import { ThemeLoader, ThemeLoaderPreview } from "js/editor/ThemeEditor/panes/ThemeLoader";
import ThemeSimple from "js/editor/ThemeEditor/panes/ThemeSimple";
import ThemeTypography from "js/editor/ThemeEditor/panes/ThemeTypography";
import { ThemePreview } from "js/editor/ThemeEditor/themePreview/ThemePreview";
import { AnimatedFullScreenContainer } from "js/editor/components/AnimatedFullScreenContainer";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowDialogAsync,
    ShowInputDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import { FlexSpacer, Gap10, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import LoadScreenIcon from "js/react/components/LoadScreenIcon";
import Spinner from "js/react/components/Spinner";
import { BlueButton, Divider, UINavigation, UINavigationItem, WhiteTextButton } from "js/react/components/UiComponents";
import { browserHistory } from "js/react/history";
import { themeColors } from "js/react/sharedStyles";

const logger = getLogger(LogGroup.THEMES);

const Container = styled(AnimatedFullScreenContainer)`
    background: #F9FAFC;
`;

const ThemeNav = styled(UINavigation)`
    width: 150px;
    display: flex;
    flex-direction: column;

    .ui-navigation-item.selected {
        background: white
    }

    #saved-themes.ui-navigation-item.selected {
        background: rgb(248, 250, 252);
    }
`;

const Template = styled.div`
  background: #ff4500;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 20px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ThemeHeader = styled.div`
    width: 100%;
    height: 50px;
    background: #333;
    color: white;
    display: flex;
    align-items: center;
    flex-shrink: 0;

    .MuiButton-label {
        font-size: 13px;
        color: #eee;
    }
`;

const UsedId = styled.div`
    font-size: 13px;
    color: white;
`;

const ThemeName = styled.div`
    font-size: 15px;
    color: white;
    font-weight: bold;
    cursor: ${props => props.canEdit ? "pointer" : "default"};

    &:hover {
        color: ${props => props.canEdit ? themeColors.ui_blue : "white"};
    }
`;

const ThemeFrame = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    background: #f1f1f1;
    overflow: hidden;
`;

const ThemeEditorContainer = styled.div`
    background-color: #fff;
    color: #555;
    height: 100%;
    text-transform: uppercase;
    width: 420px;
    overflow-y: scroll;
`;

const ThemeRightFrame = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;
const ThemePreviewFrame = styled.div`
    flex-grow: 1;
`;

const ThemeActions = styled.div`
    height: 70px;
    width: 100%;
    display: flex;
    padding: 10px 20px;
    align-items: center;
    justify-content: center;
    gap: 20px;
`;

class ThemeEditor extends Component {
    constructor(props) {
        super(props);

        this.state = {
            pane: null,
            theme: null,
            themeName: null,
            themeType: null,
            iconBaseUrl: props.icons?.baseUrl,
            isCreating: false,
            fonts: [],
            updateTheme: false,
            hasSavedTheme: false,
            themeModifiedAfterSave: false,
            userThemes: [],
            sharedThemes: []
        };
        this.themeFontIds = [];
    }

    get themeOrPresentationId() {
        return this.props.presentation?.id ?? this.props.themeOrPresentationId;
    }

    async componentDidMount() {
        let { themeOrPresentationId, presentation = null, t } = this.props;
        if (presentation?.id) {
            themeOrPresentationId = presentation.id;
        }

        if (themeOrPresentationId !== ThemeEditorController.themeOrPresentationId) {
            ThemeEditorController.setThemeOrPresentationId(null);
            ThemeEditorController.setActiveThemeEditorPanel("colors");
        }

        let { iconBaseUrl } = this.state;
        if (!iconBaseUrl) {
            let config = await db("config")
                .once("value")
                .then(snapshot => snapshot.val());
            iconBaseUrl = config.iconBaseUrl;
            this.setState({ iconBaseUrl });
        }

        const [userThemes, sharedThemes] = await Promise.all([
            WorkspaceController.getUserThemes(false),
            WorkspaceController.getSharedThemes(false)
        ]);

        // load presentation
        let theme, themeName, themeType, sourceTheme, teamThemeUsageCount;
        let saveButtonLabel = t("Update Presentation Theme");
        let isNewTheme = false;

        let loadTeamTheme = async themeId => {
            sourceTheme = await WorkspaceController.getSharedTheme(themeId);
            // Clone the theme so we don't modify the original
            theme = _.cloneDeep(sourceTheme.attributes);

            themeName = theme.name;
            themeType = ThemeType.TEAM;

            teamThemeUsageCount = (WorkspaceController.resources.filter(resource =>
                (resource.resourceType === WorkspaceResourceType.PRESENTATION || resource.resourceType === WorkspaceResourceType.PRESENTATION_TEMPLATE) &&
                resource.metadata.sharedThemeId === theme.id).length);
            saveButtonLabel = t("Save Team Theme");
        };

        if (themeOrPresentationId == "new-presentation") {
            // user is creating a theme during new presentation flow
            theme = _.cloneDeep(defaultSimpleTheme);
            themeName = t("New Presentation Theme");
            themeType = ThemeType.PRESENTATION;
            isNewTheme = true;
            saveButtonLabel = t("Create Presentation");
        } else if (themeOrPresentationId == "new-team-theme") {
            // user is creating a new team theme
            theme = _.cloneDeep(defaultSimpleTheme);
            themeName = uniqueName(t("New Team Theme"), sharedThemes.map(theme => theme.get("name")));
            themeType = ThemeType.TEAM;
            isNewTheme = true;
            saveButtonLabel = t("Create Team Theme");
        } else if (themeOrPresentationId == "new-saved-theme") {
            // user is creating a new user theme
            theme = _.cloneDeep(defaultSimpleTheme);
            themeName = uniqueName(t("My Theme"), userThemes.map(theme => theme.get("name")));
            themeType = ThemeType.USER;
            isNewTheme = true;
            saveButtonLabel = t("Save Theme");
        } else if (userThemes.some(theme => theme.id === themeOrPresentationId)) {
            const userTheme = await WorkspaceController.getUserTheme(themeOrPresentationId);
            // user is editing a saved theme
            sourceTheme = userTheme;
            // Clone the theme so we don't modify the original
            theme = _.cloneDeep(sourceTheme.attributes);
            themeName = theme.name;
            themeType = ThemeType.USER;
            saveButtonLabel = t("Save Theme");
        } else if (sharedThemes.some(theme => theme.id === themeOrPresentationId)) {
            await loadTeamTheme(themeOrPresentationId);
        } else {
            // user is editing a theme from a presentation
            if (!presentation) {
                presentation = await getPresentation(themeOrPresentationId, { permission: "write", autoSync: false });
            }

            if (presentation.has("sharedThemeId")) {
                await loadTeamTheme(presentation.get("sharedThemeId"));
            } else {
                theme = await app.themeManager.loadTheme(presentation);
                theme = theme.attributes;
                themeName = t("Theme for {{presentationName}}", { presentationName: presentation.get("name") });
                themeType = ThemeType.PRESENTATION;
            }
        }

        this.setState({
            presentation,
            isNewTheme,
            theme,
            sourceTheme,
            themeName,
            themeType,
            saveButtonLabel,
            teamThemeUsageCount,
            pane: this.props.defaultPane ?? ThemeEditorController.activeThemeEditorPanel ?? "colors",
            userThemes,
            sharedThemes
        });

        await ThemeEditorController.setEditedTheme(theme);
    }

    componentWillUnmount() {
        const { presentation } = this.props;
        const { presentation: statePresentation } = this.state;

        if (statePresentation && statePresentation !== presentation) {
            statePresentation.disconnect();
        }
    }

    handleSelectPane = pane => {
        const trackingProps = {
            themeOrPresentationId: this.themeOrPresentationId
        };

        switch (pane) {
            case "quick":
                break;
            case "colors":
                trackActivity("ThemeActions", "ShowColorSettings", null, null, trackingProps);
                break;
            case "typography":
                trackActivity("ThemeActions", "ShowTypographySettings", null, null, trackingProps);
                break;
            case "decoration":
                trackActivity("ThemeActions", "ShowDecorationSettings", null, null, trackingProps);
                break;
            case "footer":
                trackActivity("ThemeActions", "ShowFooterSettings", null, null, trackingProps);
                break;
            case "loader":
                trackActivity("ThemeActions", "ShowLoaderSettings", null, null, trackingProps);
                break;
            case "title":
                trackActivity("ThemeActions", "ShowTitleSlideSettings", null, null, trackingProps);
                break;
        }

        this.setState({ pane });
    }

    async updateTheme(deltaTheme) {
        const { theme, hasSavedTheme } = this.state;

        // if we're updating to gradient, and not already set to gradient, save the default background color
        if (
            ["gradient", "reverseGradient"].includes(deltaTheme.styleBackground) &&
            !["gradient", "reverseGradient"].includes(theme.styleBackground)
        ) {
            deltaTheme.lastDefaultBackgroundColor = theme.defaultBackgroundColor;
        } else if (deltaTheme.defaultBackgroundColor) {
            deltaTheme.lastDefaultBackgroundColor = deltaTheme.defaultBackgroundColor;
        }

        // Make sure we don't set undefined values
        Object.entries(deltaTheme).forEach(([key, value]) => {
            if (value === undefined) {
                deltaTheme[key] = null;
            }
        });

        const editedTheme = Object.assign({}, theme, deltaTheme);
        this.setState({
            theme: editedTheme,
            updateTheme: true,
            themeModifiedAfterSave: hasSavedTheme
        });

        await ThemeEditorController.setEditedTheme(editedTheme);
    }

    importTheme = () => {
        const { t } = this.props;
        let { fonts } = this.state;

        let onlyTeamThemes = !WorkspaceController.actionPermissions[ActionPermissionsObject.CUSTOM_THEMES].use;
        let hadLoadedFonts = !!fonts?.length;

        ShowDialog(ChooseThemeDialog, {
            title: t("Import Theme"),
            onlyTeamThemes,
            showEditCurrentTheme: false,
            callback: async newTheme => {
                if (await ShowConfirmationDialog({ title: t("Are you sure you want to import this theme?"), message: t("The current theme properties will be overwritten by the imported theme.") })) {
                    this.themeFontIds = newTheme.getFontIds();
                    if (hadLoadedFonts) {
                        this.setState({
                            fonts: []
                        });
                        // Wait a frame
                        await delay(10);
                    }

                    const themeAttributes = _.omit(newTheme.attributes, [
                        "id", "hidden", "isBuiltIn", "name", "thumbnailId", "userId", "cjkFont"
                    ]);

                    // make sure these properties are arrays
                    themeAttributes.backgroundGradients = themeAttributes.backgroundGradients || [];
                    themeAttributes.backgroundImages = themeAttributes.backgroundImages || [];

                    const {
                        originalFontBodyFontId,
                        originalFontHeaderFontId,
                        originalFontTitleFontId,
                        playerBranding,
                        ...deltaTheme
                    } = themeAttributes;

                    if (originalFontBodyFontId) deltaTheme.originalFontBodyFontId = originalFontBodyFontId;

                    if (originalFontHeaderFontId) deltaTheme.originalFontHeaderFontId = originalFontHeaderFontId;

                    if (originalFontTitleFontId) deltaTheme.originalFontTitleFontId = originalFontTitleFontId;

                    // playerBranding should only exist on team themes
                    if (playerBranding) deltaTheme.playerBranding = playerBranding;

                    // imageStyle should be set to default if it doesn't exist in the new theme
                    if (!deltaTheme.imageStyle || !deltaTheme.imageStylePrompt) {
                        deltaTheme.imageStyle = AiImageStyleTypes.MINIMALIST;
                        deltaTheme.imageStylePrompt = AiImageStyles[AiImageStyleTypes.MINIMALIST].prompt;
                    }

                    await this.updateTheme(deltaTheme);
                }
            }
        });
    };

    importPPTTheme = async () => {
        const { t } = this.props;
        let { fonts } = this.state;

        if (
            await ShowConfirmationDialog({
                title: t("Import colors and fonts from a PowerPoint"),
                message: t("Start with your brand colors and fonts in place, and customize your theme from there."),
                okButtonLabel: t("Select File")
            })
        ) {
            let importedThemeProperties = await importPPTTheme("theme");

            let headerFont = fonts.find(font => importedThemeProperties.headerFont.startsWith(font.label));
            let bodyFont = fonts.find(font => importedThemeProperties.bodyFont.startsWith(font.label));

            let headerFontId = headerFont?.id;
            let headerFontWeight = importedThemeProperties.headerFontWeight;
            let bodyFontId = bodyFont?.id;
            let bodyFontWeight = importedThemeProperties.bodyFontWeight;

            if (!headerFont || !bodyFont) {
                await ThemeEditorController.loadFonts();
                let results = await ShowDialogAsync(ImportFontsDialog, {
                    headerFont: importedThemeProperties.headerFont,
                    headerFontWeight: importedThemeProperties.headerFontWeight,
                    bodyFont: importedThemeProperties.bodyFont,
                    bodyFontWeight: importedThemeProperties.bodyFontWeight,
                });
                if (results) {
                    headerFontId = results.headerFont.fontId;
                    headerFontWeight = results.headerFont.weight;
                    bodyFontId = results.bodyFont.fontId;
                    bodyFontWeight = results.bodyFont.weight;
                } else {
                    return;
                }
            }

            this.updateTheme({
                colors: importedThemeProperties.colors,
                fontHeaderFontId: headerFontId,
                fontHeaderWeight: headerFontWeight,
                fontHeaderScaling: 100,
                fontHeaderLetterSpacing: 0,
                fontHeaderLineSpacing: 1.6,
                fontBodyFontId: bodyFontId,
                fontBodyWeight: bodyFontWeight,
                fontBodyScaling: 100,
                fontBodyLetterSpacing: 0,
                fontBodyLineSpacing: 1.6,
                fontTitleFontId: headerFontId,
                fontTitleWeight: headerFontWeight,
                fontTitleScaling: 100,
                fontTitleLetterSpacing: 0,
                fontTitleLineSpacing: 1.6
            });
        }
    };

    handleEditThemeName = async () => {
        const { t } = this.props;
        const { theme, themeType, themeName } = this.state;

        if (themeType == ThemeType.TEAM || themeType == ThemeType.USER) {
            const newName = await ShowInputDialog({
                title: t("Enter a name for your theme"),
                value: themeName,
                trimInput: true
            });
            if (newName) {
                theme.name = newName;
                this.setState({ themeName: newName, themeNameWasChanged: true });
            }
        }
    }

    handleSaveChanges = async () => {
        const { themeOrPresentationId, onBeforeSave, t } = this.props;
        const { theme, isNewTheme, themeName, presentation, themeType, sourceTheme, teamThemeUsageCount, hasSavedTheme, themeModifiedAfterSave } = this.state;
        const controllerThemeOrPresentationId = presentation?.id ?? themeOrPresentationId;

        // Default to "colors" pane for future editing sessions
        ThemeEditorController.setActiveThemeEditorPanel("colors");
        // if we are changing a PPT addin we do not have themeOrPresentationId, so we need to set it to the passed presentation id
        ThemeEditorController.setThemeOrPresentationId(controllerThemeOrPresentationId);
        ThemeEditorController.setEditedTheme(null);

        this.setState({ isCreating: true });

        switch (themeType) {
            case ThemeType.USER:
                onBeforeSave?.();

                if (isNewTheme) {
                    await app.themeManager.createUserTheme(theme, themeName);
                } else {
                    try {
                        sourceTheme.update({
                            ...theme,
                            thumbnailId: await GenerateThemeThumbnail(theme)
                        }, { replaceKeys: true });
                    } catch (err) {
                        logger.error(err, "[ThemeEditor] error generating thumbnail for new User Theme", { themeId: theme.id });
                    }
                }
                this.handleNavigate();
                break;
            case ThemeType.TEAM:
                if (isNewTheme) {
                    let newName = await ShowInputDialog({ title: t("Enter a name for your new team theme"), value: themeName });
                    // Input dialog returns false if user cancels
                    if (newName === false) {
                        this.setState({ isCreating: false });
                        return;
                    }

                    onBeforeSave?.();
                    theme.name = newName ?? theme.name;

                    let newTheme = await app.themeManager.createTeamTheme(theme, theme.name);
                    try {
                        newTheme.update({ thumbnailId: await GenerateThemeThumbnail(theme) });
                    } catch (err) {
                        logger.error(err, "[ThemeEditor] error generating thumbnail for new Team Theme", { themeId: newTheme.id });
                    }
                } else {
                    if (teamThemeUsageCount > 0) {
                        let save = await ShowConfirmationDialog({
                            title: t("Are you sure you want to save changes to this Team Theme?"),
                            message: t("There {{presentationCount}} will be affected by this change.", {
                                presentationCount: pluralize(
                                    t("is ## presentation"),
                                    teamThemeUsageCount,
                                    t("are ## presentations")
                                )
                            })
                        });
                        if (!save) {
                            this.handleNavigate();
                            return;
                        }
                    }

                    onBeforeSave?.();
                    sourceTheme.update(theme, { replaceKeys: true });
                    sourceTheme.update({ thumbnailId: await GenerateThemeThumbnail(sourceTheme.attributes) });
                }

                this.handleNavigate();
                break;
            case ThemeType.PRESENTATION:
                onBeforeSave?.();

                let presentationId = presentation?.id;

                if (isNewTheme) {
                    if (!hasSavedTheme || themeModifiedAfterSave) {
                        this.handleCreatePresentationAndTheme();
                        return;
                    }
                    const createTheme = await app.themeManager.createUserTheme(theme, themeName);

                    const newPresentation = await PresentationLibraryController.createPresentation({
                        workspaceId: AppController.workspaceId,
                        libraryFilter: PresentationLibraryController.filter,
                        theme: createTheme
                    });
                    await app.themeManager.saveThemeToPresentation(createTheme, newPresentation);
                    presentationId = newPresentation.id;
                } else {
                    await app.themeManager.saveThemeToPresentation({ attributes: theme }, presentation);
                }

                this.handleNavigate(presentationId);

                break;
        }
    }

    handleCreatePresentationAndTheme = async () => {
        const { t } = this.props;
        const { theme, themeName, userThemes, sharedThemes } = this.state;

        let hasCustomFont = false;
        const styles = ["Body", "Title", "Header"];

        for (const style of styles) {
            const fontId = theme[`font${style}FontId`];
            if (fontId && !builtInFonts[fontId]) {
                hasCustomFont = true;
            } else {
                const obsoleteFontName = theme[`font${style}`];
                if (obsoleteFontName && !obsoleteBuiltInFontIds.includes(obsoleteFontName)) {
                    hasCustomFont = true;
                }
            }
        }
        if (hasCustomFont) {
            if (!WorkspaceController.actionPermissions[ActionPermissionsObject.CUSTOM_FONTS].use) {
                this.setState({ isCreating: false });
                ShowWarningDialog({
                    title: t("Cannot Save Theme"),
                    message: t("This theme has custom fonts - in order to save this theme, you will need to upgrade your account.")
                });
                return;
            }
        }

        ShowDialog(InputConfirmationDialog, {
            title: t("Would you like to save your new theme?"),
            message: t("Save your theme to your theme library for reuse in other presentations."),
            okButtonLabel: t("Save"),
            cancelButtonLabel: t("Not Now"),
            input: {
                type: "text",
                placeholder: t("Enter theme name..."),
                value: uniqueName(t("My Theme"), userThemes.map(theme => theme.get("name")))
            },
            cancelCallback: async () => {
                // User clicked "Not Now", just create presentation without saving theme
                const createdTheme = new Theme(_.omit(theme, "id"), { disconnected: true, autoLoad: false });

                const newPresentation = await PresentationLibraryController.createPresentation({
                    workspaceId: AppController.workspaceId,
                    libraryFilter: PresentationLibraryController.filter,
                    theme: createdTheme
                });

                await app.themeManager.saveThemeToPresentation(createdTheme, newPresentation);

                this.handleNavigate(newPresentation.id);
            },
            acceptCallback: async themeName => {
                // reserved name for ui
                const reservedName = "Theme";
                if (themeName === reservedName) {
                    ShowWarningDialog({
                        title: t("Cannot Save Theme"),
                        message: t('You can\'t use "{{name}}" as your theme name.', { name: reservedName })
                    });
                    return;
                }

                const createdTheme = await app.themeManager.createUserTheme(theme, themeName);

                this.setState({ hasSavedTheme: true, themeModifiedAfterSave: false });

                trackActivity("Theme", "NewUserTheme", null, null, { theme_id: createdTheme.id, theme_name: themeName }, { audit: false });

                const newPresentation = await PresentationLibraryController.createPresentation({
                    workspaceId: AppController.workspaceId,
                    libraryFilter: PresentationLibraryController.filter,
                    theme: createdTheme
                });
                await app.themeManager.saveThemeToPresentation(createdTheme, newPresentation);

                this.handleNavigate(newPresentation.id);
            }
        });
    }

    handleNavigate(presentationId = null) {
        const { closeDialog, returnPath } = this.props;
        // if we open the theme editor as a dialog, add logic there after closing the editor
        if (closeDialog) {
            closeDialog(true);
        } else if (returnPath) {
            AppController.goToRoute(returnPath);
        } else if (presentationId) {
            // keeping it just if a user removes the returnPath from the URL and then refreshes the page - it will still work
            // but will go to the first slide
            AppController.showEditor({ presentationId });
        } else {
            browserHistory.goBack();
        }
    }

    handleBackButton = () => {
        this.handleCancel();
    }

    handleCancel = () => {
        const { closeDialog } = this.props;

        if (closeDialog) {
            closeDialog(false);
        } else {
            browserHistory.goBack();
        }
    };

    handleSaveToLibrary = () => {
        const { t } = this.props;
        const { theme, userThemes } = this.state;

        let hasCustomFont = false;
        const styles = ["Body", "Title", "Header"];

        for (const style of styles) {
            const fontId = theme[`font${style}FontId`];
            if (fontId && !builtInFonts[fontId]) {
                hasCustomFont = true;
            } else {
                const obsoleteFontName = theme[`font${style}`];
                if (obsoleteFontName && !obsoleteBuiltInFontIds.includes(obsoleteFontName)) {
                    hasCustomFont = true;
                }
            }
        }
        if (hasCustomFont) {
            if (!WorkspaceController.actionPermissions[ActionPermissionsObject.CUSTOM_FONTS].use) {
                ShowWarningDialog({
                    title: t("Cannot Save Theme"),
                    message: t("This theme has custom fonts - in order to save this theme, you will need to upgrade your account.")
                });
                return;
            }
        }

        ShowDialog(InputConfirmationDialog, {
            title: t("Save theme to library"),
            message: t("You can save this theme to your own saved theme library for reuse in other presentations."),
            okButtonLabel: t("Save"),
            input: {
                type: "text",
                placeholder: t("Enter theme name..."),
                value: uniqueName(t("My Theme"), userThemes.map(theme => theme.get("name")))
            },
            acceptCallback: async themeName => {
                // reserved name for ui
                const reservedName = "Theme";
                if (themeName === reservedName) {
                    ShowWarningDialog({
                        title: t("Cannot Save Theme"),
                        message: t('You can\'t use "{{name}}" as your theme name.', { name: reservedName })
                    });
                    return;
                }

                const newTheme = await app.themeManager.createUserTheme(theme, themeName);
                this.setState({ hasSavedTheme: true, themeModifiedAfterSave: false });
                trackActivity("Theme", "NewUserTheme", null, null, { theme_id: newTheme.id, theme_name: themeName }, { audit: false });
            }
        });
    };

    renderPane = () => {
        const { pane, theme, iconBaseUrl } = this.state;

        let childProps = {
            ...this.props,
            theme,
            iconBaseUrl,
            update: deltaThemeAttributes => this.updateTheme(deltaThemeAttributes),
        };

        switch (pane) {
            case "quick":
                return <ThemeSimple {...childProps} />;
            case "colors":
                return <ThemeColors {...childProps} />;
            case "typography":
                return <ThemeTypography {...childProps} />;
            case "decoration":
                return <ThemeDecoration {...childProps} />;
            case "footer":
                return <ThemeFooter {...childProps} />;
            case "loader":
                return <ThemeLoader {...childProps} />;
            case "default-slides":
                return <DefaultSlides {...childProps} />;
        }
    }

    renderPreview = () => {
        const { pane, theme } = this.state;
        let childProps = {
            ...this.props,
            theme
        };

        switch (pane) {
            case "loader":
                return (
                    <ThemePreviewFrame theme={theme}>
                        <ThemeLoaderPreview {...childProps} />
                    </ThemePreviewFrame>);
            default:
                return (
                    <ThemePreviewFrame>
                        <ThemePreview theme={theme} />
                    </ThemePreviewFrame>);
        }
    }

    renderThemeName = () => {
        const { t } = this.props;
        const { themeName, themeType, teamThemeUsageCount, isNewTheme } = this.state;

        if (themeType == ThemeType.TEAM) {
            return (<>
                {themeType == ThemeType.TEAM && <Template>{t("Team Theme")}</Template>}
                <Gap10 />
                {!isNewTheme && <ThemeName id="theme-name-editor" canEdit={themeType == ThemeType.TEAM || themeType == ThemeType.USER}
                    onClick={this.handleEditThemeName}>{themeName}</ThemeName>}
                <Gap10 />
                {(themeType == ThemeType.TEAM && teamThemeUsageCount > 0) && <UsedId>
                    {t("Used in {{presentationCount}}", {
                        presentationCount: pluralize(
                            t("## presentation"),
                            teamThemeUsageCount,
                            t("## presentations")
                        )
                    })}
                </UsedId>}
            </>);
        }

        return (<>
            <Gap20 />
            <ThemeName canEdit={themeType == ThemeType.TEAM || themeType == ThemeType.USER}
                onClick={this.handleEditThemeName}>{themeName}</ThemeName>
        </>);
    };

    render() {
        const { pane, theme, themeType, saveButtonLabel, isCreating } = this.state;
        const { t } = this.props;

        if (!theme) {
            return <Container><Spinner /></Container>;
        }

        return (
            <Container onEscapeKey={this.handleCancel} id="theme-editor">
                <ThemeNav
                    selected={pane}
                    onSelect={this.handleSelectPane}
                    setAsPlaceholder={true}
                    fullHeight
                >
                    <UINavigationItem
                        hidden
                        id="quick"
                        title={t("Quick Theme")}
                        icon="auto_fix_high"
                        vertical
                    />
                    <Divider hidden color="#444" margin={15} />
                    <UINavigationItem
                        id="colors"
                        title={t("Colors")}
                        icon="color_lens"
                        vertical
                    />
                    <UINavigationItem
                        id="typography"
                        title={t("Typography")}
                        icon="format_size"
                        vertical
                    />
                    <UINavigationItem
                        id="decoration"
                        title={t("Decoration")}
                        icon="gradient"
                        vertical
                    />
                    <UINavigationItem
                        id="footer"
                        title={t("Footer")}
                        icon="video_label"
                        vertical
                    />
                    <UINavigationItem
                        id="default-slides"
                        title={t("Default Slides")}
                        icon="slide_library"
                        vertical
                    />
                    {themeType == ThemeType.TEAM &&
                        <UINavigationItem
                            id="loader"
                            title={t("Load Screen")}
                            image={<LoadScreenIcon />}
                            vertical
                        />}
                </ThemeNav>

                <FlexBox fill vertical top left>
                    <>
                        <ThemeHeader>
                            {this.renderThemeName()}
                            <FlexSpacer />
                            <WhiteTextButton
                                onClick={this.importTheme}>
                                <Icon>playlist_add</Icon>
                                {t("Import Theme...")}
                            </WhiteTextButton>
                            <Gap20 />
                            <WhiteTextButton
                                onClick={this.importPPTTheme}>
                                <Icon>upload</Icon>
                                {t("Import from PPT...")}
                            </WhiteTextButton>
                            <Gap20 />
                            <WhiteTextButton
                                onClick={this.handleSaveToLibrary}>
                                <Icon>star</Icon>
                                {t("Add to Saved Themes...")}
                            </WhiteTextButton>

                        </ThemeHeader>
                        {pane != "default-slides" &&
                            <ThemeFrame>
                                <ThemeEditorContainer>
                                    {this.renderPane()}
                                </ThemeEditorContainer>

                                <ThemeRightFrame>
                                    {this.renderPreview()}
                                    <ThemeActions>
                                        <FlexSpacer />
                                        <Button onClick={this.handleCancel}>{t("Cancel")}</Button>
                                        <BlueButton
                                            data-cy="theme-editor-create-btn"
                                            onClick={this.handleSaveChanges}
                                            disabled={isCreating}>{saveButtonLabel}</BlueButton>
                                    </ThemeActions>
                                </ThemeRightFrame>
                            </ThemeFrame>
                        }
                        {pane == "default-slides" && (
                            <ThemeFrame>
                                <FlexBox vertical left top fill>
                                    {this.renderPane()}
                                    <ThemeActions>
                                        <FlexSpacer />
                                        <Button onClick={this.handleCancel}>{t("Cancel")}</Button>
                                        <BlueButton
                                            data-cy="theme-editor-create-btn"
                                            onClick={this.handleSaveChanges}
                                            disabled={isCreating}>{saveButtonLabel}</BlueButton>
                                    </ThemeActions>
                                </FlexBox>
                            </ThemeFrame>
                        )}
                    </>
                </FlexBox>
            </Container>
        );
    }
}

export default ThemeEditorController.withState(
    withTranslation()(ThemeEditor)
);
